import {
  Button,
  Divider,
  Paper,
  Stack,
  Typography,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useState } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import ReactPlayer from 'react-player';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import { fetchInterviewResult } from 'src/services/interview.service';
import LoadingDots from 'src/components/loading-dots';
import PropTypes from 'prop-types';
import { useResponsive } from 'src/hooks/use-responsive';
import FallbackUI from './fallback-ui';
import HorizontalBar from './horizontal-bar-chart';
import ProfileLoader from './loader';
import { CandidateProfileContext } from './context/candidate-profile-context';

const useStyles = makeStyles(theme => ({
  leftContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    gap: 34,
    width: '30%',
  },
  rightContainerAnalysis: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    padding: '18px 15px',
    paddingBottom: 0,
    width: '100%',
  },
  questionsDiv: {
    gap: 24,
  },
  questionItem: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
  },
  candidateCapturesDiv: {
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
    padding: 20,
    gap: 16,
  },
  transcript: {
    lineHeight: 'normal',
    color: theme.palette.content.primaryBlack2,
  },
  scoreBtn: {
    color: theme.palette.content.accent1,
    background: theme.palette.background.primaryAccent2,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryAccent2,
    },
  },
  disabledScoreBtn: {
    color: theme.palette.content.primaryBlack3,
    background: theme.palette.background.primaryGrey2,
    fontWeight: 600,
  },
  recordedBtn: {
    color: theme.palette.content.primaryBlack1,
    background: theme.palette.background.primaryGrey1,
    '&:hover': {
      backgroundColor: theme.palette.background.primaryGrey1,
    },
  },
  crossDiv: {
    background: theme.palette.background.primaryGrey3,
    padding: 4,
    borderRadius: 32,
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    top: '4%',
    left: '92%',
    cursor: 'pointer',
    zIndex: 10,
  },
}));

function getRandomElements(arr, numElements) {
  const shuffledArray = arr.slice();

  for (let i = shuffledArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffledArray[i], shuffledArray[j]] = [shuffledArray[j], shuffledArray[i]];
  }

  return shuffledArray.slice(0, numElements);
}

const scoreState = 'scoreState';
const videoState = 'videoState';
const screenSharingState = 'screenSharingState';
const audioState = 'audioState';

export default function AiInterviewGroup({ jobId }) {
  // hooks
  const classes = useStyles();
  const { obj: candidateDetails, token } = useContext(CandidateProfileContext);
  const mdUp = useResponsive('up', 'md');

  // states
  const [interviewDetails, setInterviewDetails] = useState(null);
  const [recordedDataState, setRecordedDataState] = useState(null);
  const [skillAnalysis, setSkillAnalysis] = useState(null);
  const [candidateCaptures, setCandidateCaputures] = useState(null);
  const [aiInterviewLoader, setAiInterviewLoader] = useState(true);
  const [interviewStatus, setInterviewStatus] = useState('UNATTEMPTED');

  // effects
  useEffect(() => {
    setAiInterviewLoader(true);

    fetchInterviewResult(
      jobId,
      candidateDetails?.interview_details?.interview_id,
      token,
    )
      .then(res => {
        setSkillAnalysis(res?.body?.skill_scores);
        setInterviewStatus(res?.body?.interview_status);

        const _questions = res?.body?.questions.map(question => ({
          questionId: question?.question_id,
          question: question?.text,
          solution: null,
        }));

        const _solutions = res?.body?.solutions;

        _solutions?.forEach(solution => {
          const currentQuestion = _questions.find(
            item => item.questionId === solution.question_id,
          );
          if (currentQuestion) {
            const _solution = {
              transcript: solution?.transcript,
              candidateCaptures: solution?.candidate_captures,
              audioUrl: solution?.audio_url,
              score: solution?.score,
              isEvaluated: solution?.is_evaluated,
              video: solution?.video_record_url,
              screenRecording: res?.body?.screen_recordings?.find(
                item => item?.question_id === solution?.questionId,
              )?.screen_recording_url,
            };

            currentQuestion.solution = _solution;
          }
        });

        setInterviewDetails(_questions);

        const _candidateCaptures = [];

        _questions?.forEach(item => {
          item?.solution?.candidateCaptures?.forEach(capture =>
            _candidateCaptures.push(capture),
          );
        });

        if (_candidateCaptures.length > 8) {
          const selectedElements = getRandomElements(_candidateCaptures, 8);
          setCandidateCaputures(selectedElements);
        } else {
          setCandidateCaputures(_candidateCaptures);
        }

        const _recordedState = _questions?.map(item => {
          const obj = {
            state: scoreState,
          };
          return obj;
        });

        setRecordedDataState(_recordedState);
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setAiInterviewLoader(false);
      });
  }, [candidateDetails?.interview_details?.interview_id, token, jobId]);

  if (aiInterviewLoader) return <ProfileLoader />;

  return interviewStatus !== 'UNATTEMPTED' &&
    interviewDetails &&
    interviewDetails?.length > 0 ? (
    <Stack spacing="30px">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing="12px">
        <Stack
          direction={{
            md: 'column',
            xs: 'row',
          }}
        >
          <Stack
            spacing="17px"
            px={{
              md: '26px',
              xs: '26px',
            }}
            py={{
              md: '26px',
              xs: '26px',
            }}
            width={{
              xs: '50%',
              md: '120px',
            }}
            height={{
              md: '120px',
            }}
            alignItems={{
              xs: 'center',
              md: 'flex-start',
            }}
            justifyContent={{
              xs: 'center',
              md: 'flex-start',
            }}
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
            borderBottom={{ md: 'transparent' }}
          >
            <Stack spacing="10px" direction="row">
              <Typography variant="body1">Rank</Typography>
              <Image width="20px" src="/assets/circleI.svg" />
            </Stack>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.rank || '_'}
            </Typography>
          </Stack>
          <Stack
            spacing="17px"
            px={{
              md: '26px',
              xs: '26px',
            }}
            py={{
              md: '26px',
              xs: '26px',
            }}
            width={{
              xs: '50%',
              md: '120px',
            }}
            height={{
              md: '120px',
            }}
            alignItems={{
              xs: 'center',
              md: 'flex-start',
            }}
            justifyContent={{
              xs: 'center',
              md: 'flex-start',
            }}
            border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
          >
            <Typography
              variant={mdUp ? 'subheading1' : 'subheading1'}
              fontSize={16}
              fontWeight={500}
            >
              Overall Score
            </Typography>
            <Typography variant="h1">
              {candidateDetails?.interview_details?.total_score === null
                ? '_'
                : candidateDetails?.interview_details?.total_score}
            </Typography>
          </Stack>
        </Stack>
        <Stack className={classes.rightContainerAnalysis}>
          <Typography variant="subheading1">Analysis</Typography>
          {skillAnalysis ? (
            <HorizontalBar skillAnalysis={skillAnalysis} />
          ) : (
            <Stack
              sx={{ height: '25vh' }}
              spacing="12px"
              alignItems="center"
              justifyContent="center"
            >
              <Image width={48} height={48} src="/assets/no-data-found.svg" />
              <Typography variant="subheading2">No data</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack className={classes.questionsDiv}>
        {interviewDetails?.map((item, index) => {
          const isActiveScore = () => {
            if (!item?.solution) return false;
            if (
              item?.solution?.isEvaluated === null ||
              item?.solution?.isEvaluated === undefined
            ) {
              return false;
            }
            return true;
          };

          const isActiveAudio = () => {
            if (!item?.solution?.audioUrl) return false;
            return true;
          };

          const isActiveScreenRecording = () => {
            if (!item?.solution?.screenRecording) return false;
            return true;
          };

          const isActiveVideo = () => {
            if (!item?.solution?.video) return false;
            return true;
          };

          let showState = '';

          if (item?.solution?.video) {
            showState = videoState;
          } else if (item?.solution?.screenRecording) {
            showState = screenSharingState;
          } else if (item?.solution?.audioUrl) {
            showState = audioState;
          }

          return (
            <Stack key={index} className={classes.questionItem}>
              <Typography
                variant="subheading1"
                lineHeight="normal"
                fontSize={{
                  xs: '16px',
                  md: '16px',
                }}
                mb="12px"
              >
                Question {index + 1} : {item?.question}
              </Typography>
              <Stack>
                {showState === videoState && (
                  <Stack position="relative">
                    <ReactPlayer
                      width="100%"
                      height="255px"
                      // style={{height:"100px"}}
                      controls
                      url={item?.solution?.video}
                    />
                    {/* <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: '12px',
                        top: '12px',
                        bgcolor: theme => theme.palette.background.primaryGrey2,
                        color: theme => theme.palette.content.primaryBlack1,
                      }}
                      onClick={() => {
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = null;
                          return dumData;
                        });
                      }}
                    >
                      <Iconify icon="tabler:x" />
                    </IconButton> */}
                  </Stack>
                )}
                {showState === screenSharingState && (
                  <Stack
                    spacing="5px"
                    sx={{ borderRadius: '6px', overflow: 'hidden' }}
                  >
                    {/* <Stack
                      className={classes.crossDiv}
                      justifyContent="flex-end"
                      onClick={() =>
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = scoreState;
                          return dumData;
                        })
                      }
                    >
                      <Iconify icon="tabler:x" />
                    </Stack> */}
                    <ReactPlayer
                      width="100%"
                      height="255px"
                      // style={{height:"100px"}}
                      controls
                      url={item?.solution?.screenRecording}
                    />
                  </Stack>
                )}
                {showState === audioState && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    position="relative"
                    backgroundColor="#F3F3F3"
                    borderRadius="24px"
                    paddingRight="28px"
                  >
                    <ReactAudioPlayer
                      src={item.solution?.audioUrl}
                      style={{
                        width: '100%',
                        height: '40px',
                      }}
                      controls
                    />
                    {/* <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: '8px',
                      }}
                      onClick={() => {
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          dumData[index].state = null;
                          return dumData;
                        });
                      }}
                    >
                      <Iconify icon="tabler:x" />
                    </IconButton> */}
                  </Stack>
                )}
              </Stack>
              <Stack spacing="8px" mt="16px">
                {item?.solution &&
                (item?.solution?.isEvaluated !== null ||
                  item?.solution?.isEvaluated !== undefined) ? (
                  <Typography
                    variant="input"
                    fontWeight={500}
                    color={!item?.solution?.isEvaluated && 'info'}
                    className={classes.transcript}
                  >
                    {!item?.solution?.isEvaluated ? (
                      <Stack
                        sx={{
                          color: theme => theme.palette.content.accent1,
                        }}
                        direction="row"
                        alignItems="baseline"
                        spacing="2px"
                      >
                        Evaluation under process
                        <LoadingDots color="#5418EF" />
                      </Stack>
                    ) : (
                      `Transcript : ${item?.solution?.transcript}`
                    )}
                  </Typography>
                ) : (
                  <Typography
                    color={theme => theme.palette.content.primaryRed1}
                    variant="input"
                    fontWeight={500}
                  >
                    Unanswered
                  </Typography>
                )}
                <Stack direction="row" spacing="12px">
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!isActiveScore()}
                    className={
                      isActiveScore()
                        ? classes.scoreBtn
                        : classes.disabledScoreBtn
                    }
                  >
                    Score - {item?.solution?.score || 0}
                  </Button>

                  {/* {item?.solution?.video && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!isActiveVideo()}
                      className={classes.recordedBtn}
                      startIcon={
                        <Iconify
                          style={{ cursor: 'pointer' }}
                          icon="tabler:video"
                        />
                      }
                      sx={{
                        border: theme =>
                          recordedDataState[index].state === videoState &&
                          `1px solid ${theme.palette.border.primaryAccent1}`,
                      }}
                      onClick={() =>
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          if (dumData[index].state === videoState) {
                            dumData[index].state = null;
                          } else {
                            dumData[index].state = videoState;
                          }
                          return dumData;
                        })
                      }
                    >
                      Video
                    </Button>
                  )}
                  {item?.solution?.screenRecording && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!isActiveScreenRecording()}
                      className={classes.recordedBtn}
                      startIcon={<Iconify icon="tabler:screen-share" />}
                      sx={{
                        border: theme =>
                          recordedDataState[index].state ===
                            screenSharingState &&
                          `1px solid ${theme.palette.border.primaryAccent1}`,
                      }}
                      onClick={() =>
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          if (dumData[index].state === screenSharingState) {
                            dumData[index].state = null;
                          } else {
                            dumData[index].state = screenSharingState;
                          }
                          return dumData;
                        })
                      }
                    >
                      Screen Sharing
                    </Button>
                  )}
                  {item?.solution?.audioUrl && (
                    <Button
                      variant="contained"
                      size="small"
                      disabled={!isActiveAudio()}
                      className={classes.recordedBtn}
                      startIcon={<Iconify icon="tabler:microphone" />}
                      sx={{
                        border: theme =>
                          recordedDataState[index].state === audioState &&
                          `1px solid ${theme.palette.border.primaryAccent1}`,
                      }}
                      onClick={() =>
                        setRecordedDataState(data => {
                          const dumData = [...data];
                          if (dumData[index].state === audioState) {
                            dumData[index].state = null;
                          } else {
                            dumData[index].state = audioState;
                          }
                          return dumData;
                        })
                      }
                    >
                      Audio
                    </Button>
                  )} */}
                </Stack>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      {candidateCaptures && candidateCaptures?.length > 0 && (
        <Stack className={classes.candidateCapturesDiv}>
          <Typography
            variant="subheading1"
            fontSize={{
              xs: '16px',
              md: '16px',
            }}
          >
            Proctor monitoring
          </Typography>
          <Stack
            direction="row"
            alignItems={{
              xs: 'center',
              md: 'flex-start',
            }}
            justifyContent={{
              xs: 'center',
              md: 'flex-start',
            }}
            sx={{ flexWrap: 'wrap', gap: '10px' }}
          >
            {candidateCaptures?.map((candidateCapture, index) => (
              <Paper
                key={index}
                sx={{
                  mr: '6px',
                  borderRadius: 1,
                  position: 'relative',
                }}
              >
                <Stack alignItems="center" sx={{ pb: 0.5 }}>
                  <Image
                    src={candidateCapture?.image_url}
                    sx={{
                      width: mdUp ? 150 : 120,
                      height: mdUp ? 100 : 80,
                      borderRadius: 1.5,
                    }}
                    alt="image"
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  ) : (
    <FallbackUI content="No data found" />
  );
}

AiInterviewGroup.propTypes = {
  jobId: PropTypes.string.isRequired,
};
